import { Box, type GridProps, HStack, Text } from "@chakra-ui/layout";
import { Avatar, Tooltip } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  BrandRevenueGroupByEnum,
  BrandRevenueOrderByColumnEnum,
} from "@src/__generated__/urql-graphql";
import type { DetailSummary } from "@src/components/modules/reports/project/common/types";
import { type BodyRow, ButtonLink, Link } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { UncollapseOnHover } from "@src/components/ui-kit/UncollapseOnHover";
import { WrapComponent } from "@src/utils/components/WrapComponent";
import { toApiDate } from "@src/utils/dates";
import type { ReactNode } from "react";
import { BrandReportBaseModel } from "./models";
import type {
  BrandProfitAndRevenueSubRow,
  BrandProfitSubRow,
  BrandReportColumn,
  BrandRevenueSubRow,
  CreateBrandColumnsParams,
} from "./types";

export const DETAIL_TEMPLATE_COLUMN: GridProps["gridTemplateColumns"] =
  "1fr repeat(4, 0.85fr)";

type BrandClientRow = BodyRow &
  (
    | BrandReportBaseModel
    | BrandRevenueSubRow
    | BrandProfitSubRow
    | BrandProfitAndRevenueSubRow
  );

function renderHeaderWithTooltip(
  label: ReactNode,
  tooltip?: ReactNode,
): ReactNode {
  if (!tooltip) {
    return label;
  }

  return (
    <HStack spacing="1">
      <Box>{label}</Box>
      <Tooltip
        label={
          <Box minW="100px" maxW="300px" p="3">
            {tooltip}
          </Box>
        }
      >
        <Box>
          <Icon
            color="#919EAB"
            name="info-circle"
            style={{ transform: "scale(0.8)" }}
            variant="solid"
          />
        </Box>
      </Tooltip>
    </HStack>
  );
}

function createHref(
  pathname: string,
  query: Record<string, string | undefined>,
): { pathname: string; query: Record<string, string | undefined> } {
  return { pathname, query };
}

function isBrandParentRow(
  row: BrandClientRow,
): row is BodyRow & BrandReportBaseModel {
  return row instanceof BrandReportBaseModel;
}

function getThumbnail(row: BrandClientRow): string | undefined {
  if (isBrandParentRow(row)) {
    return row.image ?? undefined;
  }

  return row.image?.urls.thumbnail ?? undefined;
}

export function withBrandHeaderTooltip(
  label: ReactNode,
  tooltip?: ReactNode,
): () => ReactNode {
  return () => renderHeaderWithTooltip(label, tooltip);
}

export function createBrandColumns<Row extends BrandClientRow>({
  detailPathname,
  from,
  to,
  groupBy,
}: CreateBrandColumnsParams): BrandReportColumn<Row>[] {
  const isGroupedByBrand = groupBy === BrandRevenueGroupByEnum.None;
  const fromDate = toApiDate(from);
  const toDate = toApiDate(to);

  return [
    {
      id: "client",
      meta: { label: isGroupedByBrand ? t`Brand` : t`Client` },
      header: isGroupedByBrand ? t`Brand` : t`Client`,
      orderBy: BrandRevenueOrderByColumnEnum.BrandName,
      cell: ({ row }) => {
        const original = row.original;
        const isParent = isBrandParentRow(original);
        const detailHref = createHref(detailPathname, {
          brandId: !isParent || isGroupedByBrand ? original.id : undefined,
          clientId: isParent && !isGroupedByBrand ? original.id : undefined,
          from: fromDate,
          to: toDate,
        });
        const entityHref = createHref(
          !isParent || isGroupedByBrand ? "/brands/detail" : "/clients/detail",
          { id: original.id },
        );

        return (
          <UncollapseOnHover
            width="24"
            dataComponentName="client-detail-button"
            hiddenComponent={
              <ButtonLink ml="2" href={detailHref} variant="outline" size="xs">
                <Trans>Show details</Trans>
              </ButtonLink>
            }
          >
            <Link href={entityHref}>
              <WrapComponent
                if={!isParent || isGroupedByBrand}
                with={(children) => (
                  <HStack spacing="2">
                    <Avatar src={getThumbnail(original)} />
                    {children}
                  </HStack>
                )}
              >
                <Text>{original.name}</Text>
              </WrapComponent>
            </Link>
          </UncollapseOnHover>
        );
      },
    },
  ];
}

export const EMPTY_SUMMARY: DetailSummary = {
  billings: 0,
  netMargin: 0,
  netProfit: 0,
};
